import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus, RefreshCw, AlertTriangle, HeartPulse } from "lucide-react";

const fmt = (d) => (d ? String(d).split("-").reverse().join("/") : "—");

const FIELD_LABEL = {
  dorsal: "Dorsal", position: "Posición", secondary_position: "2ª posición",
  birth_year: "Año nac.", phone: "Teléfono", phone_mother: "Tel. madre",
  phone_father: "Tel. padre", medical_expiry: "R. médico", rffm_player_code: "Código RFFM",
};

export default function ImportPreviewDialog({ preview, onClose, onConfirm, loading }) {
  const [decisions, setDecisions] = useState({});
  if (!preview) return null;
  const { summary, rows } = preview;
  const setDec = (idx, patch) =>
    setDecisions((d) => ({ ...d, [idx]: { ...(d[idx] || {}), ...patch } }));

  const news = rows.filter((r) => r.action === "create");
  const updates = rows.filter((r) => r.action === "update");
  const unchanged = rows.filter((r) => r.action === "unchanged");
  const reviews = rows.filter((r) => r.action === "review");
  const medConflicts = rows.filter((r) => r.medical_conflict);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle>Vista previa de la importación</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
          {[["Total", summary.total, "bg-slate-100 text-slate-700"],
            ["Nuevos", summary.new, "bg-emerald-100 text-emerald-800"],
            ["Actualizar", summary.safe_updates, "bg-blue-100 text-blue-800"],
            ["Sin cambios", summary.unchanged, "bg-slate-100 text-slate-500"],
            ["A revisar", summary.review, "bg-amber-100 text-amber-800"]].map(([l, v, c]) => (
            <div key={l} className={`rounded-lg p-2 ${c}`} data-testid={`import-summary-${l}`}>
              <div className="text-xl font-black">{v}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold">{l}</div>
            </div>
          ))}
        </div>

        {reviews.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-3" data-testid="import-review-section">
            <div className="flex items-center gap-2 text-sm font-black text-amber-900">
              <AlertTriangle className="h-4 w-4" /> Posibles duplicados: decide qué hacer
            </div>
            {reviews.map((r) => (
              <div key={r.index} className="rounded-lg bg-white border border-amber-200 p-3 space-y-2" data-testid={`review-row-${r.index}`}>
                <div className="text-xs">
                  <b>Archivo:</b> {r.doc.name}
                  {r.doc.birth_year ? ` · ${r.doc.birth_year}` : ""}
                  {r.doc.dorsal ? ` · dorsal ${r.doc.dorsal}` : ""}
                </div>
                <div className="text-xs">
                  <b>Existente:</b> {r.existing?.name}
                  {r.existing?.birth_year ? ` · ${r.existing.birth_year}` : ""}
                  {r.existing?.dorsal ? ` · dorsal ${r.existing.dorsal}` : ""}
                </div>
                <div className="text-[11px] text-amber-700">{r.reason}</div>
                <Select value={decisions[r.index]?.action || "skip"} onValueChange={(v) => setDec(r.index, { action: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`review-action-${r.index}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="update">Es el mismo jugador: actualizar</SelectItem>
                    <SelectItem value="create">Es otro jugador: crear nuevo</SelectItem>
                    <SelectItem value="skip">Omitir este registro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {medConflicts.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="import-medical-conflicts">
            <div className="flex items-center gap-2 text-sm font-black text-rose-900">
              <HeartPulse className="h-4 w-4" /> Reconocimiento médico: fechas distintas
            </div>
            {medConflicts.map((r) => (
              <label key={r.index} className="flex items-center gap-2 text-xs bg-white rounded-lg border border-rose-200 p-2">
                <input type="checkbox"
                  checked={!!decisions[r.index]?.use_file_medical}
                  onChange={(e) => setDec(r.index, { use_file_medical: e.target.checked })}
                  data-testid={`medical-use-file-${r.index}`} />
                <span>
                  <b>{r.doc.name}</b>: guardada {fmt(r.medical_conflict.old)} → archivo {fmt(r.medical_conflict.new)}.
                  Marcar para usar la fecha del archivo (si no, se conserva la actual).
                </span>
              </label>
            ))}
          </div>
        )}

        {updates.length > 0 && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3" data-testid="import-updates-section">
            <div className="flex items-center gap-2 text-sm font-black text-blue-900 mb-1">
              <RefreshCw className="h-4 w-4" /> Se actualizarán ({updates.length})
            </div>
            <ul className="space-y-0.5 max-h-40 overflow-y-auto">
              {updates.map((r) => (
                <li key={r.index} className="text-[11px] text-slate-700">
                  • <b>{r.existing?.name}</b>
                  {Object.entries(r.diffs || {}).map(([f, v]) =>
                    ` · ${FIELD_LABEL[f] || f}: ${v.old ?? "—"} → ${v.new}`).join("")}
                </li>
              ))}
            </ul>
          </div>
        )}

        {news.length > 0 && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="import-new-section">
            <div className="flex items-center gap-2 text-sm font-black text-emerald-900 mb-1">
              <UserPlus className="h-4 w-4" /> Jugadores nuevos ({news.length})
            </div>
            <div className="text-[11px] text-slate-700 max-h-32 overflow-y-auto">
              {news.map((r) => r.doc.name).join(" · ")}
            </div>
          </div>
        )}

        {unchanged.length > 0 && (
          <p className="text-[11px] text-slate-400">{unchanged.length} jugador(es) sin cambios.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} data-testid="import-cancel-btn">Cancelar</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            disabled={loading}
            onClick={() => onConfirm(decisions)}
            data-testid="import-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Confirmar importación
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
